/* @flow */
import React, { Component } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import ClickOutside from "../common/ClickOutside";
import MoneyRaw from "../Money";
import { tournamentCurrency } from "../Icons/Kuchka";

const Container = styled.div`
  ${({ tourBorder }) =>
    tourBorder &&
    css`
      border-radius: 6px;
      ${tourBorder.border};
    `};

  ${({ tourBorderAnimation }) =>
    tourBorderAnimation &&
    tourBorderAnimation.animation()};
`;

const DropdownButton = styled.div`
  position: absolute;
  top: 50%;
  right: 9px;
  transform: translateY(-50%);
  border: 4px solid transparent;
  border-top-color: #99959d;
  border-bottom: none;
  z-index: 20;
`;

// prettier-ignore
const SelectView = styled.div`
  position: relative;
`;

const Value = styled.div`
  position: relative;
  width: 100%;
  padding: 6px 9px 5px;
  font-size: 16px;
  color: ${({ theme }) => theme.foreground};
  border: 1px solid ${props => props.theme.borderMainColor};
  border-radius: 4px;
  background: ${props => props.theme.mainDark};
  box-shadow: inset 0px 3px 0px 0px rgba(0, 0, 0, 0.08);
  appearance: none;
  cursor: pointer;

  ${({ invalid, theme }) =>
    invalid &&
    css`
      border-color: ${theme.lower};
      box-shadow: inset 0px 3px 0px 0px rgba(0, 0, 0, 0.08),
        inset 0px 0px 0px 2px ${theme.lower};
    `};
  @media (max-width: 959px) {
    padding: 5px 8px 5px;
  }
`;

const ValueInput = styled(Value)`
  padding-top: 4px;
  padding-bottom: 1px;
  margin: 0;
  line-height: 1;
  font-family: Roboto, sans-serif;

  @media (max-width: 959px) {
    padding-top: 5px;
    padding-bottom: 5px;
  }
`;

const OptionList = styled.div`
  position: absolute;
  top: 100%;
  width: 100%;
  overflow: auto;
  padding: 8px 0;
  color: ${({ theme }) => theme.foreground};
  background: ${props => props.theme.mainDark};
  z-index: 1337;

  @media (max-width: 959px) {
    font-size: 16px;
  }
`;

const OptionView = styled.div`
  padding: 4px 18px;
  cursor: pointer;

  :hover {
    background: rgba(255, 255, 255, 0.1);
  }

  ${props =>
    props.active &&
    css`
      background: rgba(255, 255, 255, 0.1);
    `};
  @media (max-width: 959px) {
    padding: 5px 8px 5px;
    font-size: 16px;
  }
`;

const Money = styled(MoneyRaw)`
  & span span {
    font-size: ${({ theme }) =>
      theme.locale === "ar" ? theme.minFontSize : "14px"};
    color: #99959d;

    @media (max-width: 959px) {
      font-size: ${({ theme }) =>
        theme.locale === "ar" ? theme.minFontSize : "12px"};
    }
  }
`;

type SelectProps<V> = {
  value: V,
  invalid?: boolean,
  suffix?: string,
  onChange: (value: V) => void,
  children?: Function
};

type Context<V> = {
  selectValue: V,
  onChange: (value: V) => void
};

const contextTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func
};

type SelectState = {
  isOpen: boolean
};

export default class SelectWithInput extends Component<
  SelectProps<*>,
  SelectState
> {
  static childContextTypes = contextTypes;

  state = {
    isOpen: false
  };

  handleChange = (nextValue: any) => {
    this.setState({ isOpen: false });
    this.props.onChange(nextValue);
  };

  handleInputChange = (event: SyntheticInputEvent<>) => {
    this.props.onChange(event.target.value);
  };

  handleKeyPress = (event: SyntheticKeyboardEvent<>) => {
    if (event.key === "Enter") {
      this.collapse();
      event.preventDefault();
    }
  };

  collapse = () => {
    this.setState({ isOpen: false });
  };

  getChildContext() {
    return {
      value: this.props.value,
      onChange: this.handleChange
    };
  }

  render() {
    const { value, children, invalid } = this.props;
    const { isOpen } = this.state;

    const options =
      isOpen && typeof children === "function" ? children() : null;

    return (
      <Container
        tourBorder={
          !this.props.isOriginal &&
          this.props.tour &&
          this.props.tour.borderStyles
        }
        tourBorderAnimation={
          !this.props.isOriginal &&
          this.props.tour &&
          this.props.tour.outside &&
          this.props.tour.borderStyles
        }
      >
        {isOpen ? (
          <ClickOutside onOutsideClick={this.collapse}>
            {({ innerRef }) => (
              <SelectView ref={innerRef}>
                <DropdownButton />
                <ValueInput
                  as="input"
                  ref={input => input && input.focus()}
                  type="tel"
                  value={value}
                  invalid={invalid}
                  onChange={this.handleInputChange}
                  onKeyPress={this.handleKeyPress}
                  disabled={this.props.disabled}
                />
                {options}
              </SelectView>
            )}
          </ClickOutside>
        ) : (
          <Value
            onClick={() => {
              this.setState({ isOpen: true });
            }}
            invalid={invalid}
          >
            <DropdownButton />
            <Money
              currency={
                this.props.tradeMode === "tournament"
                  ? tournamentCurrency
                  : null
              }
            >
              {value}
            </Money>
          </Value>
        )}
      </Container>
    );
  }
}

type OptionListProps = {
  children?: any
};

type OptionProps = {
  value?: any,
  children?: any
};

export function Options({ children }: OptionListProps) {
  return <OptionList>{children}</OptionList>;
}

export function Option(
  { children, value }: OptionProps,
  { selectValue, onChange }: Context<*>
) {
  const meaningfulValue = typeof value !== "undefined" ? value : children;

  const isActive = meaningfulValue === selectValue;

  return (
    <OptionView
      active={isActive}
      onClick={() => {
        onChange(meaningfulValue);
      }}
    >
      {children}
    </OptionView>
  );
}

Option.contextTypes = contextTypes;



// WEBPACK FOOTER //
// ./src/components/TradeControls/SelectWithInput.js